import { useState, useEffect, useCallback, useRef } from 'react';

import { AnimationControls, useAnimationControls } from 'framer-motion';

const useCarousel = <T extends HTMLElement>(
	carousel_ref: HTMLElement | null,
	carousel_inner_ref: HTMLElement | null,
	dependency_array: Array<any>,
): [AnimationControls, { right: number; left: number }, 'x' | 'y' | boolean] => {
	const [drag_constraints, setDragConstraints] = useState({ right: 0, left: 0 });
	const [drag_condition, setDragCondition] = useState<'x' | 'y' | boolean>(false);
	const control = useAnimationControls();

	// 드래그 가능 여부 계산 함수
	const calculateDragCondition = useCallback(() => {
		if (!carousel_inner_ref || !carousel_ref) return false;
		return carousel_inner_ref.scrollWidth - carousel_ref.offsetWidth > 0 ? 'x' : false;
	}, [carousel_inner_ref, carousel_ref]);

	// 드래그 제약 조건 계산
	useEffect(() => {
		if (!carousel_inner_ref || !carousel_ref) return;

		const width = carousel_inner_ref.scrollWidth - carousel_ref.offsetWidth;
		setDragConstraints({ right: 0, left: -width });

		// 드래그 조건 업데이트
		setDragCondition(calculateDragCondition());
	}, [carousel_inner_ref, carousel_ref, calculateDragCondition]);

	// 드래그 조건 및 애니메이션 설정
	useEffect(() => {
		if (!carousel_inner_ref || !carousel_ref) return;

		const transform = carousel_inner_ref?.style.transform;
		const transform_matched = transform?.match(/translateX*\(-?\d+.?\d{0,}px\)/) ?? '';
		const translateX = +transform_matched[0]?.split('translateX(')[1]?.split('px)')[0];

		if (-translateX > carousel_inner_ref.scrollWidth - carousel_ref.offsetWidth) {
			control.start({
				x: -(carousel_inner_ref.scrollWidth - carousel_ref.offsetWidth),
			});
		}
	}, [carousel_inner_ref, carousel_ref, control]);

	// 클릭 이벤트 방지 (드래그 중 클릭 방지)
	useEffect(() => {
		if (!carousel_inner_ref) return;

		const arr = carousel_inner_ref.children;

		for (let i = 0; i < arr.length; i++) {
			let x: number;
			arr[i].addEventListener('mousedown', (e: Event) => {
				const _e = e as MouseEvent;
				x = _e.clientX;
			});
			arr[i].addEventListener('click', (e: Event) => {
				const _e = e as MouseEvent;
				if (_e.clientX !== x) _e.stopImmediatePropagation();
			});
		}
	}, [carousel_inner_ref]);

	return [control, drag_constraints, drag_condition];
};

export default useCarousel;
